import { useRef } from 'react'
import { MoreVertical, Pencil, Trash2, Upload } from 'lucide-react'
import {
  Popover,
  PopoverMenu,
  PopoverMenuDivider,
  PopoverMenuGroup,
  PopoverMenuItem,
  PopoverTrigger,
} from '@/components/ui/Popover/Popover'
import { toast } from '@/components/ui'
import type { CofaKind, DocumentRow } from '../api/types'
import {
  useDeleteCofaLot,
  useRemoveCofa,
  useRenameCofa,
  useReplaceCofa,
} from '../api/queries'

export interface DocumentActionsMenuProps {
  row: DocumentRow
  canAct: boolean
  /** Opens the batch uploader (Chunk B: shared with /cofa toolbar). */
  onRequestBatchUpload: (row: DocumentRow) => void
}

/**
 * Per-row `⋯` menu exposed on List + Table views (admin + regulatory
 * gated). Restored in Sprint 04 — previously the only way to mutate a
 * CofA from `/cofa-docs` was to open the preview drawer, which meant
 * admins clicked through every row one-by-one.
 *
 * Two action groups, one per CofA kind (vendor / SPI), plus a
 * "Upload CofAs…" item that launches the batch uploader. Rename +
 * Remove disable themselves when the target kind has no file attached.
 *
 * The hidden file input is per-row (DOM noise is fine; centralizing
 * would need an app-scoped Zustand store — not worth the plumbing for
 * one feature). `e.stopPropagation()` on the trigger prevents the
 * parent row's click-to-preview from firing.
 */
export function DocumentActionsMenu({
  row,
  canAct,
  onRequestBatchUpload,
}: DocumentActionsMenuProps) {
  const rename = useRenameCofa()
  const remove = useRemoveCofa()
  const replace = useReplaceCofa()
  const deleteLot = useDeleteCofaLot()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pendingKindRef = useRef<CofaKind | null>(null)

  const mutationPending =
    rename.isPending || remove.isPending || replace.isPending || deleteLot.isPending

  function onRename(kind: CofaKind) {
    const attachment = kind === 'vendor_cofa' ? row.vendor_cofa : row.spi_cofa
    if (!attachment) return
    const next = window.prompt('Rename CofA', attachment.filename)
    if (!next || next === attachment.filename) return
    rename.mutate(
      { lotId: row.lot_id, kind, newFilename: next },
      {
        onSuccess: () => toast.success(`Renamed ${attachment.filename}`),
        onError: (err) =>
          toast.danger(
            `Rename failed${err instanceof Error ? `: ${err.message}` : ''}`,
          ),
      },
    )
  }

  function onRemove(kind: CofaKind) {
    const attachment = kind === 'vendor_cofa' ? row.vendor_cofa : row.spi_cofa
    if (!attachment) return
    const ok = window.confirm(
      `Remove ${kind === 'vendor_cofa' ? 'vendor' : 'SPI'} CofA from ${
        row.lot_name
      }? Clears the attachment but keeps the document in the middleware library.`,
    )
    if (!ok) return
    remove.mutate(
      { lotId: row.lot_id, kind },
      {
        onSuccess: () =>
          toast.success(
            `Removed ${kind === 'vendor_cofa' ? 'vendor' : 'SPI'} CofA from ${row.lot_name}`,
          ),
        onError: (err) =>
          toast.danger(
            `Remove failed${err instanceof Error ? `: ${err.message}` : ''}`,
          ),
      },
    )
  }

  function onReplace(kind: CofaKind) {
    pendingKindRef.current = kind
    fileInputRef.current?.click()
  }

  function onDeleteLot() {
    const ok = window.confirm(
      `Permanently delete lot ${row.lot_name} (${row.product_name})?\n\n` +
        `This removes the Odoo lot record itself — not just a CofA ` +
        `attachment. It cannot be undone, and only succeeds if the lot ` +
        `has no real stock activity (no receipts, shipments, or on-hand ` +
        `quantity). Use this for phantom lots created by a wrong-product ` +
        `CofA match — not for lots that ever held real inventory.`,
    )
    if (!ok) return
    deleteLot.mutate(
      { lotId: row.lot_id },
      {
        onSuccess: () => toast.success(`Deleted lot ${row.lot_name}`),
        onError: (err) =>
          toast.danger(
            `Delete failed${err instanceof Error ? `: ${err.message}` : ''}`,
          ),
      },
    )
  }

  function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    const kind = pendingKindRef.current
    e.target.value = ''
    pendingKindRef.current = null
    if (!file || !kind) return
    replace.mutate(
      { file, lotId: row.lot_id, kind },
      {
        onSuccess: (data: unknown) => {
          toast.success(
            `Replaced ${kind === 'vendor_cofa' ? 'vendor' : 'SPI'} CofA for ${row.lot_name}`,
          )
          if (
            data &&
            typeof data === 'object' &&
            (data as { spi_cofa_pending?: boolean }).spi_cofa_pending
          ) {
            toast.info('SPI CofA regeneration dispatched')
          }
        },
        onError: (err) =>
          toast.danger(
            `Replace failed${err instanceof Error ? `: ${err.message}` : ''}`,
          ),
      },
    )
  }

  return (
    <>
      <Popover>
        <PopoverTrigger
          type="button"
          onClick={(e) => e.stopPropagation()}
          disabled={!canAct || mutationPending}
          title={canAct ? 'CofA actions' : 'Admin / regulatory / operations required'}
          aria-label="CofA actions"
          className={
            'inline-flex h-7 w-7 items-center justify-center rounded-sm border border-transparent text-tertiary ' +
            (canAct
              ? 'cursor-pointer hover:border-border-default hover:bg-surface-hover hover:text-primary'
              : 'cursor-not-allowed opacity-30')
          }
        >
          <MoreVertical size={14} />
        </PopoverTrigger>
        <PopoverMenu
          align="end"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <PopoverMenuGroup label="Vendor CofA">
            <PopoverMenuItem
              icon={Pencil}
              disabled={!row.vendor_cofa}
              onSelect={() => onRename('vendor_cofa')}
            >
              Rename
            </PopoverMenuItem>
            <PopoverMenuItem
              icon={Upload}
              onSelect={() => onReplace('vendor_cofa')}
            >
              {row.vendor_cofa ? 'Replace' : 'Upload single file'}
            </PopoverMenuItem>
            <PopoverMenuItem
              icon={Trash2}
              danger
              disabled={!row.vendor_cofa}
              onSelect={() => onRemove('vendor_cofa')}
            >
              Remove
            </PopoverMenuItem>
          </PopoverMenuGroup>
          <PopoverMenuDivider />
          <PopoverMenuGroup label="SPI CofA">
            <PopoverMenuItem
              icon={Pencil}
              disabled={!row.spi_cofa}
              onSelect={() => onRename('spi_cofa')}
            >
              Rename
            </PopoverMenuItem>
            <PopoverMenuItem
              icon={Upload}
              onSelect={() => onReplace('spi_cofa')}
            >
              {row.spi_cofa ? 'Replace' : 'Upload single file'}
            </PopoverMenuItem>
            <PopoverMenuItem
              icon={Trash2}
              danger
              disabled={!row.spi_cofa}
              onSelect={() => onRemove('spi_cofa')}
            >
              Remove
            </PopoverMenuItem>
          </PopoverMenuGroup>
          <PopoverMenuDivider />
          <PopoverMenuItem
            icon={Upload}
            onSelect={() => onRequestBatchUpload(row)}
          >
            Upload CofAs (batch)…
          </PopoverMenuItem>
          <PopoverMenuDivider />
          <PopoverMenuItem icon={Trash2} danger onSelect={onDeleteLot}>
            Delete lot…
          </PopoverMenuItem>
        </PopoverMenu>
      </Popover>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        hidden
        onChange={onFileChosen}
      />
    </>
  )
}
